import { throwAppError } from '@interactiveshell/base-utils';
import type { InteractiveShellAppCommand } from '@interactiveshell/commands';
import { commandKeyToContext } from '@interactiveshell/constants';
import {
  type CommandExposedServices,
  useCoreServices,
} from '@interactiveshell/context';

import type { InferType, Validator } from '@interactiveshell/mini-js-utils';
import type {
  Command,
  CommandHandler,
  CommandHandlerContext,
  CommandKey,
  Store,
} from '@interactiveshell/types';
import { useEffect } from 'react';

type CommandArgs<C extends Command> = C['args'] extends null
  ? null
  : {
      [K in keyof C['args']]: C['args'][K] extends Validator<unknown>
        ? InferType<C['args'][K]>
        : never;
    };

export function c<const T extends InteractiveShellAppCommand['id']>(
  id: T,
  handler: (
    services: Pick<
      CommandExposedServices,
      Extract<InteractiveShellAppCommand, { id: T }>['dependencies']['services'][number]
    >,
    args: CommandArgs<Extract<InteractiveShellAppCommand, { id: T }>>,
    commandKey: CommandKey<NoInfer<T>>,
  ) => void | Promise<void>,
): {
  id: T;
  handler: CommandHandler;
} {
  return {
    id,
    handler: handler as CommandHandler,
  };
}

export function useC<T extends InteractiveShellAppCommand['id']>(
  id: T,
  handler: (
    services: Pick<
      CommandExposedServices,
      Extract<InteractiveShellAppCommand, { id: T }>['dependencies']['services'][number]
    >,
    args: CommandArgs<Extract<InteractiveShellAppCommand, { id: T }>>,
    commandKey: CommandKey<NoInfer<T>>,
  ) => void | Promise<void>,
) {
  const coreServices = useCoreServices();

  useEffect(() => {
    const unregister = coreServices.commandRegistry.registerHandler({
      id,
      handler: handler as CommandHandler,
    });
    return unregister;
  }, [id, coreServices, handler]);
}

export type ChildDispatcher<TId extends InteractiveShellAppCommand['id']> = (
  id: TId,
  args: CommandArgs<Extract<InteractiveShellAppCommand, { id: TId }>>,
) => void;

type ChildCommandsIds<T extends string> = Extract<
  InteractiveShellAppCommand,
  { id: T }
>['dependencies'] extends {
  commands?: Array<infer C>;
}
  ? C extends string
    ? C
    : never
  : never;

export function getCtx<T extends string>(
  key: CommandKey<T>,
): {
  store: Store;
  dispatch: <TInput extends ChildCommandsIds<T>>(
    id: TInput,
    args: CommandArgs<Extract<InteractiveShellAppCommand, { id: TInput }>>,
  ) => void;
} {
  const result = commandKeyToContext.get(key);
  if (!result) {
    throwAppError('error::command:unregistered', 'Command not registered', {
      commandId: key.key,
    });
  }
  return {
    dispatch: result.context.dispatch,
    store: result.context.store,
  } satisfies CommandHandlerContext;
}
